import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Ticket, Trash2, Power } from 'lucide-react';
import AdminSection from '../components/AdminSection';
import Pagination from '../components/Pagination';
import { usePaged } from '../hooks/use-paged';
import { fetchCoupons, createCoupon, updateCoupon, deleteCoupon } from '../lib/adminApi';

const inputCls =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-[#1fa5a3] focus:ring-4 focus:ring-[#1fa5a3]/10';

const EMPTY = { code: '', discount_type: 'percent', discount_value: '', valid_until: '', max_uses: '', min_amount: '', active: true };

export default function AdminCoupons() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [msg, setMsg] = useState({ ok: '', err: '' });
  const listQuery = useQuery({ queryKey: ['admin', 'coupons'], queryFn: fetchCoupons });
  const coupons = listQuery.data?.items || [];
  const { pageItems: pagedCoupons, page, setPage, pageCount, pageSize, setPageSize } = usePaged(coupons, 20);
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'coupons'] });

  const buildPayload = () => ({
    code: form.code.trim().toUpperCase(),
    discount_type: form.discount_type,
    discount_value: Number(form.discount_value) || 0,
    active: form.active,
    valid_until: form.valid_until || null,
    max_uses: form.max_uses ? Number(form.max_uses) : null,
    min_amount: form.min_amount ? Number(form.min_amount) : null,
    activity_slugs: [],
  });

  const createMut = useMutation({
    mutationFn: () => createCoupon(buildPayload()),
    onSuccess: async () => { setMsg({ ok: 'Cupón creado.', err: '' }); setForm(EMPTY); await invalidate(); },
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo crear.' }),
  });
  const toggleMut = useMutation({
    mutationFn: (c) => updateCoupon(c.id, {
      code: c.code, discount_type: c.discount_type, discount_value: c.discount_value,
      active: !c.active, valid_until: c.valid_until, max_uses: c.max_uses,
      min_amount: c.min_amount_cents ? c.min_amount_cents / 100 : null, activity_slugs: c.activity_slugs || [],
    }),
    onSuccess: invalidate,
  });
  const delMut = useMutation({ mutationFn: (id) => deleteCoupon(id), onSuccess: invalidate });

  const submit = (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.discount_value) { setMsg({ ok: '', err: 'Código y valor son obligatorios.' }); return; }
    setMsg({ ok: '', err: '' });
    createMut.mutate();
  };

  const discountLabel = (c) => (c.discount_type === 'percent' ? `${c.discount_value}%` : `${c.discount_value} €`);

  return (
    <AdminSection eyebrow="Admin" title="Cupones" description="Códigos de descuento aplicables en el checkout (porcentaje o importe fijo)." widthClassName="max-w-6xl">
      {msg.ok ? <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{msg.ok}</div> : null}
      {msg.err ? <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{msg.err}</div> : null}

      <div className="grid gap-6 lg:grid-cols-[1fr_0.8fr]">
        {/* List */}
        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <h2 className="text-xl font-bold text-[#14213d]">{coupons.length} cupones</h2>
          <div className="mt-4 divide-y divide-slate-100">
            {pagedCoupons.map((c) => (
              <div key={c.id} className="flex items-center gap-3 py-3">
                <Ticket className={`h-5 w-5 flex-shrink-0 ${c.active ? 'text-[#1fa5a3]' : 'text-slate-300'}`} />
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-sm font-bold text-[#14213d]">{c.code}</p>
                  <p className="text-xs text-slate-500">
                    {discountLabel(c)} · usos {c.used_count || 0}{c.max_uses ? `/${c.max_uses}` : ''}
                    {c.valid_until ? ` · hasta ${c.valid_until}` : ''}{c.min_amount_cents ? ` · mín ${c.min_amount_cents / 100} €` : ''}
                  </p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase ${c.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {c.active ? 'Activo' : 'Inactivo'}
                </span>
                <button type="button" onClick={() => toggleMut.mutate(c)} className="rounded-full border border-slate-200 p-2 text-slate-500 hover:border-[#1fa5a3] hover:text-[#1fa5a3]" title="Activar/desactivar"><Power className="h-4 w-4" /></button>
                <button type="button" onClick={() => { if (window.confirm(`¿Eliminar ${c.code}?`)) delMut.mutate(c.id); }} className="rounded-full border border-slate-200 p-2 text-slate-500 hover:border-red-400 hover:text-red-500" title="Eliminar"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {!coupons.length ? <p className="py-8 text-center text-sm text-slate-500">Sin cupones todavía.</p> : null}
          </div>
          <Pagination page={page} pageCount={pageCount} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={setPageSize} pageSizes={[20, 50, 100]} />
        </section>

        {/* Create form */}
        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <h2 className="text-xl font-bold text-[#14213d]">Nuevo cupón</h2>
          <form onSubmit={submit} className="mt-4 space-y-3">
            <input className={inputCls} placeholder="Código (ej. VERANO10)" value={form.code} onChange={(e) => set('code', e.target.value.toUpperCase())} required />
            <div className="grid grid-cols-2 gap-3">
              <select className={inputCls} value={form.discount_type} onChange={(e) => set('discount_type', e.target.value)}>
                <option value="percent">Porcentaje %</option>
                <option value="fixed">Importe fijo €</option>
              </select>
              <input className={inputCls} type="number" min="0" step="0.01" placeholder={form.discount_type === 'percent' ? '% descuento' : '€ descuento'} value={form.discount_value} onChange={(e) => set('discount_value', e.target.value)} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500">Válido hasta</label>
                <input className={`${inputCls} mt-1`} type="date" value={form.valid_until} onChange={(e) => set('valid_until', e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Usos máx.</label>
                <input className={`${inputCls} mt-1`} type="number" min="1" placeholder="Ilimitado" value={form.max_uses} onChange={(e) => set('max_uses', e.target.value)} />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Pedido mínimo (€)</label>
              <input className={`${inputCls} mt-1`} type="number" min="0" step="0.01" placeholder="Sin mínimo" value={form.min_amount} onChange={(e) => set('min_amount', e.target.value)} />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} />Activo</label>
            <button type="submit" disabled={createMut.isPending} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1fa5a3] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#188b89] disabled:opacity-60">
              <Plus className="h-4 w-4" />{createMut.isPending ? 'Creando…' : 'Crear cupón'}
            </button>
          </form>
        </section>
      </div>
    </AdminSection>
  );
}
